import { Box, Button, Stack, Typography } from '@mui/material'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { RepairOrderContext } from '../../../providers/RepairOrderProvider/RepairOrderProvider'
import { useRecommendMaintenanceScheduleMutation } from '../../../redux/api/independentApi'
import { useGetCarfaxServicesQuery } from '../../../redux/api/shopMatrixApi'
import {
  LaborApplication,
  MaintenanceScheduleWithRecommendation
} from '../../../sdk'
import { SingleMaintenanceSchedule } from '../../../sdk/models/SingleMaintenanceSchedule'
import { CenteredLoading } from '../..'
import { MaintenanceScheduleContext } from './MaintenanceScheduleProvider'
import { RecommendationCard } from './RecommendationCard'
export interface MaintenanceScheduleWithRecommendationWithEntry
  extends MaintenanceScheduleWithRecommendation {
  entry: SingleMaintenanceSchedule
}
export const AiHighlights = () => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { vehicle, repairOrder } = useContext(RepairOrderContext)
  const { maintenanceSchedules, selected, setSelected } = useContext(
    MaintenanceScheduleContext
  )

  const selectedIds = useMemo(() => selected.map((s) => s.id!), [selected])
  const { data: serviceHistory, isLoading: loadingCarfax } =
    useGetCarfaxServicesQuery(
      {
        authorization: accessToken,
        shop: shopId,
        vin: vehicle?.info!.vin || ''
      },
      {
        skip: !vehicle?.info!.vin
      }
    )
  const [recommend, { isLoading }] = useRecommendMaintenanceScheduleMutation()
  const [result, setResult] = useState<
    { [key: string]: MaintenanceScheduleWithRecommendation } | undefined
  >()
  useEffect(() => {
    if (maintenanceSchedules && serviceHistory && repairOrder) {
      async function getResult() {
        const temp = await recommend({
          authorization: accessToken,
          recommendMaintenanceScheduleRequest: {
            mileage: repairOrder?.entry!.info!.mileageIn || 0,
            shop: shopId,
            maintenanceSchedule: maintenanceSchedules,
            serviceHistory
          }
        }).unwrap()
        setResult(temp)
      }
      getResult()
    }
  }, [
    accessToken,
    maintenanceSchedules,
    recommend,
    repairOrder,
    serviceHistory,
    shopId,
    vehicle?.id
  ])
  const recommendations = useMemo(() => {
    if (!result) {
      return
    }
    const temp: MaintenanceScheduleWithRecommendationWithEntry[] = []
    maintenanceSchedules?.forEach((ms) => {
      const id = ms.laborApplication?.id
      // only show recommendations for non-inspect maintenance schedules
      if (!ms.laborApplication?.taxonomy?.literalName?.includes('Inspect')) {
        if (id && result[id] && !result[id].covered) {
          // backend ai recommendations using the first timing for recommendation
          temp.push({
            ...result[id],
            entry: {
              timingInfoList: ms.timingInfoList![0],
              laborApplication: ms.laborApplication!
            }
          })
        }
      }
    })
    return temp
  }, [maintenanceSchedules, result])
  const selectedRecommendations = useMemo(
    () =>
      recommendations?.filter((r) =>
        selectedIds?.includes(r.entry!.laborApplication!.id!)
      ),
    [recommendations, selectedIds]
  )
  const handleSelectAll = useCallback(() => {
    const temp = [
      ...(selected || []),
      ...((recommendations || [])
        .filter((r) => !selectedIds?.includes(r.entry!.laborApplication!.id!))
        .map((r) => r.entry!.laborApplication) as LaborApplication[])
    ]
    setSelected(temp)
  }, [recommendations, selected, selectedIds, setSelected])
  const handleUnselectAll = useCallback(() => {
    const temp = [...(selected || [])].filter(
      (r) =>
        !(
          selectedRecommendations?.map((r) => r.entry!.laborApplication!.id) ||
          []
        ).includes(r.id!)
    )
    setSelected(temp)
  }, [selected, selectedRecommendations, setSelected])
  return (
    <Stack>
      <Stack
        sx={{ bgcolor: 'primary.light' }}
        direction="row"
        justifyContent={'space-between'}
        alignItems={'center'}>
        <Typography margin={1}>
          AI Highlights
          {recommendations ? ` (${recommendations.length})` : ''}
        </Typography>
        {selectedRecommendations && selectedRecommendations.length === 0 && (
          <Button onClick={handleSelectAll}>Select All</Button>
        )}
        {selectedRecommendations && selectedRecommendations.length > 0 && (
          <Button onClick={handleUnselectAll}>Unselect All</Button>
        )}
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{ overflowX: 'scroll', width: '100%' }}
        py={1}>
        {(loadingCarfax || isLoading) && (
          <Box sx={{ width: '100%' }}>
            <CenteredLoading />
          </Box>
        )}
        {recommendations?.map((recommendation, index) => (
          <RecommendationCard key={index} recommendation={recommendation} />
        ))}
      </Stack>
    </Stack>
  )
}
